import { SymbolNode, isSymbolNode, MathNode } from 'mathjs';
import {
  firstDefined,
  firstNonZero,
  getParentValue,
  getProperty,
  isDefined,
  maxDefined,
  getSBEFLifetime,
  getMaxHeatProductionCapacity,
} from './extentions';
import { uniq } from 'lodash';
import { isNumeric } from '../math.helpers';
import { cacheFactory } from '../function.helpers';
import { ElementPropertyName } from '../../models/element-property.model';
import { elementQuantityNames } from '../../models/element-quantities.model';
import { patchMathJSValidAlpha } from './utils/patch-mathjs-valid-alpha';
import { mathJS } from './mathjs-instance';
import {
  co2eBaseUnits,
  co2eUnitAliasMap,
  co2eUnitDefinitionMap,
  unitLessCO2eUnits,
} from '@shared/models/units/co2e-units.model';
import {
  percentAliases,
  percentBaseUnit,
} from '@shared/models/units/percent-unit.model';
import {
  currencyAliasMap,
  currencyBaseUnits,
} from '@shared/models/units/currency-units.model';
import { aliasUnitCreationData } from '@shared/models/units/alias-units.model';

export { mathJS };

const G = 9.81;

if (
  typeof mathJS.unit !== 'function' ||
  typeof mathJS.createUnit !== 'function'
) {
  throw new Error('mathjs dependencies are not installed');
}
patchMathJSValidAlpha(
  mathJS,
  new Set([
    '°', // for degrees of different kinds
    '%', // to be able to add percent
    '_',
    '-', // our currency unit aliases uses SEK_A1-A3
  ]),
);

mathJS.createUnit('N', `${1 / G} kg`, { override: true });
mathJS.createUnit('kN', `1000 N`); // doesn't seem to work to add prefixes above, maybe because of the override?

mathJS.createUnit(percentBaseUnit, {
  definition: '0.01',
  aliases: percentAliases.slice(),
});
mathJS.createUnit('ppm', '0.000001');

co2eBaseUnits.forEach((unit) => {
  mathJS.createUnit(unit, {
    ...co2eUnitDefinitionMap[unit],
    aliases: co2eUnitAliasMap[unit],
    prefixes: 'short',
  });
});
unitLessCO2eUnits.forEach((unit) => {
  mathJS.createUnit(unit, {
    ...co2eUnitDefinitionMap[unit],
    aliases: co2eUnitAliasMap[unit],
    prefixes: 'short',
  });
});

aliasUnitCreationData.forEach(({ name, definition }) => {
  mathJS.createUnit(name, definition);
});

mathJS.createUnit('°F', { definition: `${5 / 9} K`, offset: 459.67 });
mathJS.createUnit('°C', { definition: '1 K', offset: 273.15 });

// Add currency units
currencyBaseUnits.forEach((currencyUnit) => {
  const { name, config } = currencyAliasMap[currencyUnit];
  mathJS.createUnit(name, {
    ...config,
    prefixes: 'short',
  });
});

const extensions = {
  firstDefined,
  firstNonZero,
  getParentValue,
  getProperty,
  isDefined,
  maxDefined,
  getSBEFLifetime,
  getMaxHeatProductionCapacity,
} as const;

// Import extensions
mathJS.import(extensions);

/**
 * Gets all the numeric math.js functions and constants.
 * Returns them ending in an empty parenthesis ().
 */
export const getAllMathJSVariables = (): string[] => {
  const builtIn = Object.keys(mathJS).filter((key) => {
    try {
      const testExpression = `${key}(1)`;
      const res = mathJS.evaluate(testExpression) as number;

      return isNumeric(res) && typeof res === 'number' && Number.isFinite(res);
    } catch {
      return false;
    }
  });

  return [...builtIn, ...Object.keys(extensions)].map((key) => `${key}()`);
};

/**
 * Check if a variable name is valid.
 * @param name
 * @returns
 */
export const isValidVariableName = (name?: string): boolean => {
  return (
    !!name &&
    name.length < 30 &&
    /^[a-zåäö]+[a-zåäö0-9_]*$/g.test(name) &&
    isAvailableMathJsName(name)
  );
};

export const isAvailableName = (name?: string): boolean => {
  const reservedNames: string[] = [
    ...Object.values(ElementPropertyName),
    ...elementQuantityNames,
  ];

  return !!name && !reservedNames.includes(name);
};

/**
 * Get all user defined variables in an expression.
 * Cached so we only do this once per unique expression string.
 * @param expression
 * @param exclude
 * @returns
 */
export const getVariablesInExpression = (
  expression: string | undefined,
  exclude: string[] = [],
): string[] =>
  cacheFactory(
    () => {
      // Save some time if expression is empty or a number
      if (!expression || isNumeric(expression)) {
        return [];
      }

      const variables = getSymbolNodes(expression)
        .map((node) => node.name)
        .filter(isAvailableMathJsName);

      return uniq(variables).filter((v) => !exclude.includes(v));
    },
    // `exclude` MUST be part of the cache key — different excludes filter different
    // names from the same expression, and a previous call with `exclude=['x']` would
    // otherwise bleed into a later `exclude=[]` call (returning a missing variable).
    // Callers like {@link evaluateExpressionMemoKey} rely on the symbol set being complete.
    `getVariablesInExpression[${expression ?? ''}|${exclude.join(',')}]`,
    [],
  );

/**
 * Validate that a variable name is valid in math.js.
 * Also check that it's not already used as a function or constant like (PI, E, SQRT, etc.)
 * @param name
 * @returns
 */
const isAvailableMathJsName = (name: string): boolean =>
  cacheFactory(
    () => {
      try {
        // Ensure that Math.js recognize the name as ONE node ('a' not 'a-b')
        if (isSymbolNode(mathJS.parse(name))) {
          // Should crash since it shouldn't recognize the name
          const value = mathJS.evaluate(name);

          // Almost every letter is a unit so we need to allow to use unit names like kg, b, j, etc.
          if (mathJS.isUnit(value)) {
            return true;
          }
        }
      } catch (e) {
        if (e instanceof Error && e.message === `Undefined symbol ${name}`) {
          return true;
        }
      }

      return false;
    },
    `isAvailableMathJsName[${name}]`,
    [],
  );

const getMathJsNodes = (expression: string): MathNode[] => {
  const symbols: MathNode[] = [];

  try {
    mathJS.parse(expression).traverse((node) => {
      symbols.push(node);
    });
  } catch {
    // Ignore
  }
  return symbols;
};

/**
 * Get all symbol nodes in an expression.
 * @param expression
 * @returns
 */
const getSymbolNodes = (expression: string): SymbolNode[] =>
  getMathJsNodes(expression).filter(isSymbolNode);

export {
  getExpressionPropertyAccesses,
  getExpressionPropertyPathsForRoot,
  getDisallowedGetStoreyPropertyPaths,
  getInheritedExpressionSymbols,
  isAllowedGetStoreyPropertyPath,
  isExpressionPropertyPathInUse,
  type ExpressionPropertyAccess,
  type ExpressionPropertyAccessRoot,
} from './expression-property-access.helpers';
